import { toast, Bounce } from "react-toastify";
import { useCallback } from "react";

/**
 * A custom hook for displaying toast notifications with consistent configuration
 * @returns {Object} An object containing methods for different toast types
 */
const useToast = () => {
	// Default toast configuration
	const defaultConfig = {
		position: "top-right",
		autoClose: 5000,
		hideProgressBar: false,
		closeOnClick: true,
		pauseOnHover: true,
		draggable: true,
		theme: "colored",
		transition: Bounce,
	};

	/**
	 * Show a toast notification
	 * @param {string} message - The message to display
	 * @param {string} type - The type of toast (success, error, info, warning)
	 * @param {Object} customConfig - Optional custom configuration to override defaults
	 */

	
// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
const  showToast = useCallback((message, type = "info", customConfig = {}) => {
		const config = { ...defaultConfig, ...customConfig };

		switch (type) {
			case "success":
				toast.success(message, config);
				break;
			case "error":
				toast.error(message, config);
				break;
			case "warning":
				toast.warning(message, config);
				break;
			case "info":
				toast.info(message, config);
				break;
			default:
				toast(message, config);
		}
	}, []);

	/**
	 * Show a success toast
	 * @param {string} message - The success message
	 * @param {Object} customConfig - Optional custom configuration
	 */
	const success = useCallback(
		(message, customConfig = {}) => {
			showToast(message, "success", customConfig);
		},
		[showToast],
	);

	/**
	 * Show an error toast
	 * @param {string|Error} error - Error message or Error object
	 * @param {Object} customConfig - Optional custom configuration
	 */
	const error = useCallback(
		(message, customConfig = {}) => {
			showToast(message, "error", customConfig);
		},
		[showToast],
	);

	/**
	 * Show an info toast
	 * @param {string} message - The info message
	 * @param {Object} customConfig - Optional custom configuration
	 */
	const info = useCallback(
		(message, customConfig = {}) => {
			showToast(message, "info", customConfig);
		},
		[showToast],
	);

	/**
	 * Show a warning toast
	 * @param {string} message - The warning message
	 * @param {Object} customConfig - Optional custom configuration
	 */
	const warning = useCallback(
		(message, customConfig = {}) => {
			showToast(message, "warning", customConfig);
		},
		[showToast],
	);

	return {
		showToast,
		success,
		error,
		info,
		warning,
	};
};

export default useToast;
